import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import { ReactComponent as PaymentIcon } from '../../../assets/icons/amount-icon.svg';
import { ReactComponent as DangerIcon } from '../../../assets/icons/danger-icon.svg';
import { ReactComponent as RightArrow } from '../../../assets/icons/right-arrow.svg';
import { brandImage, useCurrencyText, useCurrency } from '../../../config/branding';
import config from '../../../config/config';
import { useBranding } from '../../../context/BrandingContext';
import { useWebSocket } from '../../../context/WebSocket/WebSocketProvider';
import MyButton from '../../../shared-components/button/button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ToastMessage from '../../../shared-components/toast-message/ToastMessage';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import getFirstSubdomain from '../../../shared-functions/DomiansplitFunction';
import FormatTimeTo12HoursFormat from '../../../shared-functions/FormatTime';
import {
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';

import StoreLink from './StoreLink';

interface PricingDetailsProperties {
  currentPrice: string;
  dynamicPricing: {
    price: string;
    priceType: string;
    timings: {
      startDateUtc: string | null;
      endDateUtc: string | null;
    }[];
  }[];
}

interface DurationAmount {
  from: number;
  to: number;
  mode: 'grace' | 'flatRate' | 'perMinute';
  amount: number;
}

interface IdleFeePrices {
  durationAmountList: DurationAmount[];
  ceilingValue: number;
}

export type PricingType = 'FLAT_RATE' | 'PER_MINUTE';

interface PaymentMethodProperties {
  setPaymentDetails: React.Dispatch<React.SetStateAction<any>>;
  navigateToChargingSession: () => void;
  navigateToInvoice: () => void;
  skipChargingButton: boolean;
  pricingDetails: PricingDetailsProperties;
  idleFeePrices: IdleFeePrices | null;
  appVersion: string;
}

interface PaymentDetailsProperties {
  name: string;
}

/**
 * get Idle FeeLabel
 * @param item
 * @returns
 */
const getIdleFeeLabel = (item: DurationAmount, t: any) => {
  const range = `(${item.from} - ${item.to} Mins)`;

  switch (item.mode) {
    case 'grace':
      return `${t('gracePeriod')} ${range}`;
    case 'flatRate':
      return `${t('flatRate')} ${range}`;
    case 'perMinute':
      return `${t('perMinute')} ${range}`;
    default:
      return '';
  }
};

const AMOUNT_REGEX = /(\d+(?:[.,]\d+)?\s*(?:SAR|AED|ريال))/i;

/**
 * Highlights currency amounts in a translation string with <strong> tags.
 * @param text - The translated string to parse
 * @returns Array of strings and React elements
 */
const highlightAmounts = (text: string): React.ReactNode[] =>
  text.split(AMOUNT_REGEX).map((part) =>
    AMOUNT_REGEX.test(part) ? <strong key={part}>{part}</strong> : part
  );

/**
 * Payment method component
 * @param properties
 * @returns
 */
function PaymentMethod(properties: PaymentMethodProperties) {
  const { t, i18n } = useTranslation('charger');
  const currency = useCurrencyText(t);
  const currencyCode = useCurrency();
  const webSocket = useWebSocket();
  const domain = getFirstSubdomain();
  const { brandData } = useBranding();

  const {
    navigateToChargingSession,
    setPaymentDetails,
    navigateToInvoice,
    skipChargingButton,
    pricingDetails,
    idleFeePrices,
    appVersion,
  } = properties;
  const stripe =
    (domain && brandData?.guestPaymentMethod
      ? brandData?.guestPaymentMethod
      : config.guestPaymentMode) === 'stripe'
      ? useStripe()
      : null;
  const elements = useElements();

  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [paymentDetails, setDummyPaymentDetails] = useState<PaymentDetailsProperties>({
    name: '',
  });

  const allowFreeCharging =
    skipChargingButton ||
    (domain && brandData?.guestPaymentMethod
      ? brandData?.guestPaymentMethod
      : config.guestPaymentMode) === 'free';

  /**
   * Function to handle payment method change
   */
  const handlePaymentDetailsChange = (name: string, changeValue: string | number) => {
    setDummyPaymentDetails((previousValue) => ({
      ...previousValue,
      [name]: changeValue,
    }));
  };

  /**
   * handle payment method
   */
  const handlePaymentMethod = async () => {
    if (
      !allowFreeCharging &&
      (domain && brandData?.guestPaymentMethod
        ? brandData?.guestPaymentMethod
        : config.guestPaymentMode) === 'stripe'
    ) {
      setButtonLoading(true);
      if (!stripe || !elements) {
        setButtonLoading(false);
        setToastStatus({
          openToast: true,
          showToastMessage: t('somethingWentWrong'),
        });
        console.error('Stripe has not been properly loaded yet.');
        return;
      }

      if (!paymentDetails.name) {
        setButtonLoading(false);
        setToastStatus({
          openToast: true,
          showToastMessage: t('enterCardHolder'),
        });
        return;
      }

      // Get the card details from the CardElement
      const cardNumber = elements.getElement(CardNumberElement);
      const cardExpireElement = elements.getElement(CardExpiryElement);
      const cardCvc = elements.getElement(CardCvcElement);

      if (!cardNumber || !cardExpireElement || !cardCvc) {
        console.error('CardElement is not loaded yet.');
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber, // Pass the CardNumberElement here
      });

      if (error || !paymentMethod?.id) {
        setButtonLoading(false);
        setToastStatus({
          openToast: true,
          showToastMessage: error?.message || t('invalidCardDetails'),
        });
        return;
      }

      setPaymentDetails({
        paymentMethodId: paymentMethod?.id,
        last4: paymentMethod?.card?.last4,
        expiryMonth: paymentMethod?.card?.exp_month,
        expiryYear: paymentMethod?.card?.exp_year,
        funding: paymentMethod?.card?.funding,
        brand: paymentMethod?.card?.brand,
        cardHolderName: paymentDetails.name,
      });

      // Generate new guest token
      webSocket?.generateTemporaryToken();
      navigateToChargingSession();
      setButtonLoading(false);
    } else if (
      allowFreeCharging ||
      (domain && brandData?.guestPaymentMethod
        ? brandData?.guestPaymentMethod
        : config.guestPaymentMode) === 'free'
    ) {
      // Generate new guest token
      webSocket?.generateTemporaryToken();
      navigateToChargingSession();
    } else {
      setToastStatus({
        openToast: true,
        showToastMessage: 'Something went wrong!',
      });
    }
  };

  const options = {
    style: {
      base: {
        fontSize: '13px',
        color: '#212121',
        letterSpacing: '0.025em',
        fontFamily: 'Gilroy, sans-serif',
        textAlign: i18n.dir() === 'rtl' ? 'right' : 'left', // 👈 FIX
        direction: i18n.dir(),
        '::placeholder': {
          color: '#6c757d',
          textAlign: i18n.dir() === 'rtl' ? 'right' : 'left',
        },
      },
      invalid: {
        color: '#f04438',
      },
    },
  };

  /**
   * Function to handle real time stop charging
   */
  const handleChargingStop = (socketMessage: any) => {
    removeLocalStorageItem('selectedConnector');
    removeLocalStorageItem('guestToken');
    removeLocalStorageItem('chargingStatus');
    setLocalStorageItem('paidAmount', socketMessage?.paidAmount);
    setLocalStorageItem('totalKwhConsumer', socketMessage?.powerConsumed);
    navigateToInvoice();
  };

  useEffect(() => {
    if (webSocket) {
      /**
       * Handle all the messages from web socket listener
       * @param socketMessage
       */
      const handleMessage = (socketMessage: any) => {
        if (
          socketMessage?.event === 'RealTime Charging Monitoring' &&
          socketMessage?.status === 'Charging Completed'
        ) {
          handleChargingStop(socketMessage);
        }
      };

      webSocket.webSocketService.subscribe(handleMessage);

      return () => {
        webSocket.webSocketService.unsubscribe(handleMessage);
      };
    }

    // Explicitly return undefined if webSocket is falsy
    return undefined;
  }, [webSocket]);

  return (
    <div className="payment-method-wrap">
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() =>
          setToastStatus({
            openToast: false,
            showToastMessage: '',
          })
        }
        type="error"
        message={toastStatus.showToastMessage || t('somethingWentWrong')}
        onCloseTimer={6000}
      />
      <div className="payment-method-container-with-warning">
        <div className="payment-method-container">
          <h1>{t('choosePaymentMethod')}</h1>
          <div className="credit-card-warp">
            <img src={brandImage.creditCard} alt="" className="credit-card-image" />
          </div>
          <div className={`payment-method-form ${i18n.dir() === 'rtl' ? 'rtl' : ''}`}>
            <CustomInput
              inputLabel=""
              type="text"
              placeholder={t('cardHolderName')}
              name="name"
              value={paymentDetails.name}
              onChangeValue={handlePaymentDetailsChange}
              valueRequired
            />
            <div className="stripe-element-container">
              <CardNumberElement options={options} />
            </div>
            <div className="field-single-row">
              <div className="stripe-element-container">
                <CardExpiryElement options={options} />
              </div>
              <div className="stripe-element-container">
                <CardCvcElement options={options} />
              </div>
            </div>
          </div>
        </div>
        <div className="session-fee-to-show">
          <ul className="session-fee-details">
            <li>{highlightAmounts(t('sessionFeeVerification'))}</li>
            <li>{highlightAmounts(t('sessionFeePreAuth'))}</li>
            <li>{t('sessionFeeActualCost')}</li>
            {currencyCode === 'AED' && (
              <li>{t('sessionFeeAedConversion')}</li>
            )}
          </ul>
        </div>
        {idleFeePrices && idleFeePrices?.durationAmountList?.length > 0 && (
          <div className="idle-fee-content">
            <div className="idle-fee-card">
              <div className="header">
                <DangerIcon />
                <p>{t('idleFeeTitle')}</p>
              </div>

              <p className="description">{t('idleFeeDescription')}</p>

              {/* Idle fee slabs from API */}
              {idleFeePrices?.durationAmountList.map((item) => (
                <div className="row" key={`${item.mode}-${item.from}`}>
                  <span>{getIdleFeeLabel(item, t)}</span>
                  <span>-</span>
                  <span>
                    {item.mode === 'grace' ? `${item.to} Min` : `${item.amount} ${currency}`}
                  </span>
                </div>
              ))}

              {/* Ceiling value */}
              {idleFeePrices?.ceilingValue && (
                <div className="row">
                  <span>{t('maxCeilingValue')}</span>
                  <span>-</span>
                  <span>
                    {idleFeePrices.ceilingValue} {currency}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {pricingDetails.dynamicPricing.length > 0 ? (
          <div className="pricing-details">
            {pricingDetails.dynamicPricing.map((pricing) =>
              pricing.timings
                .filter((timing) => timing.startDateUtc !== null && timing.endDateUtc !== null)
                .map((timing) => (
                  <div
                    key={`${pricing.priceType}-${timing.startDateUtc}`}
                    className="pricing-detail">
                    <div className="pricing-content">
                      <p>
                        {FormatTimeTo12HoursFormat(ConvertUTCtoHHMM(timing.startDateUtc || ''))} -{' '}
                        {FormatTimeTo12HoursFormat(ConvertUTCtoHHMM(timing.endDateUtc || ''))}
                      </p>
                      <h6>
                        {Number(pricing.price).toFixed(2)} {currency}
                      </h6>
                    </div>
                  </div>
                )),
            )}
          </div>
        ) : null}
      </div>
      <StoreLink />
      <div className="payment-button">
        <MyButton
          label={allowFreeCharging ? t('skip') : t('continue')}
          svgIcon={<RightArrow />}
          buttonType="submit"
          onClickFunc={handlePaymentMethod}
          loading={buttonLoading}
          disableButton={buttonLoading}
        />
        <div className="app-version">
          <span>{appVersion}</span>
        </div>
      </div>
    </div>
  );
}

export default PaymentMethod;
